import { CCUS061Handler } from "../handler/HandlerImpl";
import { ColumnsType } from "antd/es/table";
import {
  TableIssueAuthenType,
  TableTechnicianInfoType,
} from "../../entity/Entity";
import { Checkbox, Form, Input } from "antd";

import SortTable from "../../../../../common/components/sort-table/SortTable";
import { AUTHENTICATION_KEY } from "../../constants";
import IconInfoRed from "../../../../../assets/icons/icon-info-red.svg";
import { getExpirationDateCcus061 } from "../../../../OM/OM002/presenter/ui/MemberTable";
import MESSAGE from "../../../../../common/constants/MESSAGE";
import moment from "moment";
import helpers from "../../../../../common/helpers/common";

export const COLUMN = (handler: CCUS061Handler) => {
  const sortType = handler.sortType
  const sortBy = handler.sortBy

  const validateInputName = (
    value: string,
    setError: (message: string) => void,
    isCheckboxChecked: boolean,
    errorMessage: string
  ) => {
    if (!value) {
      setError("");
      return true;
    }

    const patterns = {
      // Khi checkbox chưa được chọn: cho phép Kanji, Hiragana, chữ số half-size, chữ alphabet half-size (cả in hoa và thường), Katakana full-size
      // Cũng cho phép dấu cách full-size (\u3000) và half-size (\s)
      allowedWithoutCheckbox:
        /^[A-Za-z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\s\u3000]+$/,
      // Các ký tự đặc biệt full-width không hợp lệ
      invalidFullWidthSpecialChars:
        /[！”＃＄％＆’＊＋，−．／：；＜＝＞？＠［￥］＾＿‘｛｜｝]/,
      // Các ký tự đặc biệt half-width không hợp lệ
      invalidHalfWidthSpecialChars: /[、。]/,
      // Khi checkbox được chọn: chỉ cho phép chữ in hoa, ký tự half-size và không cho phép dấu cách full-size (\u3000)
      allowedWithCheckbox: /^[A-Z,'\-().\/\s]+$/, // Chỉ cho phép dấu cách half-size
      invalidFullWidthSpace: /\u3000/, // Kiểm tra dấu cách full-size
    };

    // Nếu checkbox được chọn
    if (isCheckboxChecked) {
      // Kiểm tra chỉ cho phép chữ alphabet in hoa, ký tự đặc biệt half-size hợp lệ và không cho phép dấu cách full-size
      if (
        !patterns.allowedWithCheckbox.test(value) ||
        patterns.invalidFullWidthSpace.test(value)
      ) {
        setError(errorMessage);
        return false;
      }
    } else {
      // Khi checkbox không được chọn, cho phép cả full-size và half-size dấu cách
      if (!patterns.allowedWithoutCheckbox.test(value)) {
        setError(errorMessage);
        return false;
      }

      // Kiểm tra các ký tự đặc biệt không hợp lệ (full-width và half-width)
      const hasInvalidChars = [
        patterns.invalidFullWidthSpecialChars,
        patterns.invalidHalfWidthSpecialChars,
      ].some((pattern) => pattern.test(value));

      if (hasInvalidChars) {
        setError(errorMessage);
        return false;
      }
    }

    setError("");
    return true;
  };

  const validateInputNameKatakana = (
    value: string,
    setError: (message: string) => void
  ) => {
    // Ký tự hợp lệ half-size: alphabet (chữ thường & in hoa), chữ số, và các ký tự đặc biệt đã liệt kê
    const validHalfWidthPattern =
      /^[A-Za-z0-9~!@#$%^&*()_+\[\]\\;',.\/{}|:"<>?=\- ]+$/;

    // Ký tự Katakana full-size
    const validFullWidthKatakana = /^[\u30A0-\u30FF]+$/;

    // Kiểm tra xem chuỗi có chứa ký tự không hợp lệ không
    const hasInvalidChars = value.split("").some(
      (char) =>
        !validHalfWidthPattern.test(char) && // Không khớp với ký tự half-size hợp lệ
        !validFullWidthKatakana.test(char) // Không khớp với ký tự Katakana full-size
    );

    if (hasInvalidChars) {
      setError(MESSAGE.MESSAGE_CCUS061_INPUT_KATAKANA);
      return false;
    }

    // Nếu không có ký tự không hợp lệ
    setError("");
    return true;
  };

  // Helper function to check if value is empty (after trimming spaces)
  const isEmpty = (value: any) =>
    !(value && value.trim().replaceAll(" ", "").replaceAll("　", ""));

  // Helper function to check all 3 groups and clear MESSAGE_CCUS061_VALID errors if at least one group has value
  // Note: We need to pass currentField and currentValue because form state isn't updated yet when onInput fires
  const checkAndClearGroupErrors = (
    recordKey: number,
    handler: CCUS061Handler,
    currentField: string,
    currentValue: string
  ) => {
    // Get all current values for this record from form
    let lastName = handler.formTab2.getFieldValue([`record${recordKey}`, "lastName"]);
    let firstName = handler.formTab2.getFieldValue([`record${recordKey}`, "firstName"]);
    let lastNameKatakana = handler.formTab2.getFieldValue([`record${recordKey}`, "lastNameKatakana"]);
    let firstNameKatakana = handler.formTab2.getFieldValue([`record${recordKey}`, "firstNameKatakana"]);
    let confirmCode = handler.formTab2.getFieldValue([`record${recordKey}`, "confirmCode"]);

    // Override with current field value (since form hasn't updated yet)
    if (currentField === "lastName") lastName = currentValue;
    if (currentField === "firstName") firstName = currentValue;
    if (currentField === "lastNameKatakana") lastNameKatakana = currentValue;
    if (currentField === "firstNameKatakana") firstNameKatakana = currentValue;
    if (currentField === "confirmCode") confirmCode = currentValue;

    // Check if each group has value
    const hasGroup1Value = !isEmpty(lastName) || !isEmpty(firstName);
    const hasGroup2Value = !isEmpty(lastNameKatakana) || !isEmpty(firstNameKatakana);
    const hasGroup3Value = !isEmpty(confirmCode);

    // If at least one group has value, clear MESSAGE_CCUS061_VALID errors from all inputs
    if (hasGroup1Value || hasGroup2Value || hasGroup3Value) {
      const currentErrors = handler.rowErrors[recordKey];
      if (currentErrors) {
        // Only clear MESSAGE_CCUS061_VALID errors, keep other errors
        if (currentErrors.lastName === MESSAGE.MESSAGE_CCUS061_VALID) {
          handler.handleRowError(recordKey, "", "lastName");
        }
        if (currentErrors.firstName === MESSAGE.MESSAGE_CCUS061_VALID) {
          handler.handleRowError(recordKey, "", "firstName");
        }
        if (currentErrors.lastNameKatakana === MESSAGE.MESSAGE_CCUS061_VALID) {
          handler.handleRowError(recordKey, "", "lastNameKatakana");
        }
        if (currentErrors.firstNameKatakana === MESSAGE.MESSAGE_CCUS061_VALID) {
          handler.handleRowError(recordKey, "", "firstNameKatakana");
        }
        if (currentErrors.confirmCode === MESSAGE.MESSAGE_CCUS061_VALID) {
          handler.handleRowError(recordKey, "", "confirmCode");
        }
      }
    }
  };

  const columnsTab1: ColumnsType<TableTechnicianInfoType> = [
    {
      title: "No",
      dataIndex: "No",
      width: 230,
      render: (text) => <div className="text-table-ccus">{text}</div>,
    },
    {
      title: (
        <SortTable
          columnTitle="技能者名"
          sortOrder={handler.funcCheckSortOrder("fullName", sortType, sortBy)}
          onChange={(sortOrder) => handler.funcSortTable("fullName", sortOrder)}
          justifyContent="start"
        />
      ),
      dataIndex: "fullName",
      render: (_, record) => (
        <div
          className="text-table-ccus"
          style={{ color: "#605BFF", cursor: "pointer" }}
          onClick={() => handler.openPopupDetail(record.key)}
        >
          {record.fullName}
        </div>
      ),
      width: 238,
    },
    {
      title: (
        <SortTable
          columnTitle="部門"
          sortOrder={handler.funcCheckSortOrder("group", sortType, sortBy)}
          onChange={(sortOrder) => handler.funcSortTable("group", sortOrder)}
          justifyContent="start"
        />
      ),
      dataIndex: "group",
      render: (text) => <div className="text-table-ccus">{text}</div>,
      width: 238,
    },
    {
      title: "技能者ID",
      dataIndex: "ccusId",
      render: (text) => (
        <div className="text-table-ccus">
          {helpers.convertToCCUSFormat(text || "")}
        </div>
      ),
      width: 238,
    },
    {
      title: (
        <SortTable
          columnTitle="有効期限"
          sortOrder={handler.funcCheckSortOrder(
            "ccusExpiredDate",
            sortType,
            sortBy
          )}
          onChange={(sortOrder) =>
            handler.funcSortTable("ccusExpiredDate", sortOrder)
          }
          justifyContent="start"
        />
      ),
      dataIndex: "expiredDate",
      render: (text) => (
        <div className="text-table-ccus">
          {text ? getExpirationDateCcus061(text) : "-"}
        </div>
      ),
      width: 238,
    },
  ];

  const columnsTab2: ColumnsType<TableIssueAuthenType> = [
    {
      title: (
        <SortTable
          columnTitle="技能者ID"
          sortOrder={handler.funcCheckSortOrder("ccusId", sortType, sortBy)}
          onChange={(sortOrder) => handler.funcSortTable("ccusId", sortOrder)}
          justifyContent="start"
        />
      ),
      render: (_, record) => {
        return (
          <div style={{ paddingTop: 16, paddingLeft: 8 }}>
            <span
              className={
                record.state === AUTHENTICATION_KEY[1].key
                  ? "text-table-ccus-disable"
                  : "text-table-ccus"
              }
            >
              {record.ccusId}
            </span>
            <Form.Item
              name={[`record${record.key}`, `ccusId`]}
              hidden
              style={{ display: "none" }}
            >
              <Input
                disabled={record.state === AUTHENTICATION_KEY[1].key}
                size="large"
                className="input-ccus062"
              />
            </Form.Item>
          </div>
        );
      },
      width: 30,
    },
    {
      title: "外国籍",
      render: (_, record) => (
        <Form.Item
          name={[`record${record.key}`, `isForeigner`]}
          valuePropName="checked"
          style={{
            paddingTop: 8,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Checkbox
            disabled={record.state === AUTHENTICATION_KEY[1].key}
            onChange={() => {
              const lastName = handler.formTab2.getFieldValue([
                `record${record.key}`,
                "lastName",
              ]);

              const firstName = handler.formTab2.getFieldValue([
                `record${record.key}`,
                "firstName",
              ]);

              const isCheckboxChecked = handler.formTab2.getFieldValue([
                `record${record.key}`,
                "isForeigner",
              ]);

              let isValid = true;
              let isFirstNameValid = true;

              if (!lastName && !firstName) {
                handler.handleRowError(record.key, "", "lastName");
                handler.handleRowError(record.key, "", "firstName");
                handler.setNotSubmit(false);
                return;
              }

              if (lastName) {
                isValid = validateInputName(
                  lastName,
                  (errorMessage: string) => {
                    handler.handleRowError(
                      record.key,
                      errorMessage,
                      "lastName"
                    );
                  },
                  isCheckboxChecked,
                  MESSAGE.MESSAGE_CCUS061_VALID_LAST_NAME
                );
              } else {
                handler.handleRowError(record.key, "", "lastName");
              }

              if (firstName) {
                isFirstNameValid = validateInputName(
                  firstName,
                  (errorMessage: string) => {
                    handler.handleRowError(
                      record.key,
                      errorMessage,
                      "firstName"
                    );
                  },
                  isCheckboxChecked,
                  MESSAGE.MESSAGE_CCUS061_VALID_FIRST_NAME
                );
              } else {
                handler.handleRowError(record.key, "", "firstName");
              }

              handler.setNotSubmit(!(isValid || isFirstNameValid));
            }}
          />
        </Form.Item>
      ),
      width: 80,
    },
    {
      title: "技能者姓①",
      render: (_, record) => (
        <Form.Item
          name={[`record${record.key}`, `lastName`]}
          className="input-ccus062-container"
          style={{
            paddingTop: 8,
            paddingLeft: 8,
          }}
          help={
            handler.rowErrors[record.key]?.lastName
              ? handler.rowErrors[record.key]?.lastName
              : ""
          }
          validateStatus={
            handler.rowErrors[record.key]?.lastName ? "error" : ""
          }
        >
          <Input
            disabled={record.state === AUTHENTICATION_KEY[1].key}
            size="large"
            className="input-ccus062"
            style={{ width: 125 }}
            onInput={(e: any) => {
              const { value } = e.target;
              if (!value) {
                handler.handleRowError(record.key, "", "lastName");
                handler.setNotSubmit(false);
                // Check and clear group errors if at least one group has value
                checkAndClearGroupErrors(record.key, handler, "lastName", "");
                return;
              }

              const isCheckboxChecked = handler.formTab2.getFieldValue([
                `record${record.key}`,
                "isForeigner",
              ]);

              const isValid = validateInputName(
                value,
                (errorMessage: string) => {
                  handler.handleRowError(record.key, errorMessage, "lastName");
                },
                isCheckboxChecked,
                MESSAGE.MESSAGE_CCUS061_VALID_LAST_NAME
              );

              handler.setNotSubmit(!isValid);

              // Check and clear group errors if at least one group has value
              checkAndClearGroupErrors(record.key, handler, "lastName", value);
            }}
          />
        </Form.Item>
      ),
      width: 170,
    },
    {
      title: "技能者名①",
      render: (_, record) => (
        <Form.Item
          name={[`record${record.key}`, `firstName`]}
          className="input-ccus062-container"
          style={{
            paddingTop: 8,
            paddingLeft: 8,
          }}
          help={
            handler.rowErrors[record.key]?.firstName
              ? handler.rowErrors[record.key]?.firstName
              : ""
          }
          validateStatus={
            handler.rowErrors[record.key]?.firstName ? "error" : ""
          }
        >
          <Input
            disabled={record.state === AUTHENTICATION_KEY[1].key}
            size="large"
            className="input-ccus062"
            style={{ width: 125 }}
            onInput={(e: any) => {
              const { value } = e.target;
              if (!value) {
                handler.handleRowError(record.key, "", "firstName");
                handler.setNotSubmit(false);
                // Check and clear group errors if at least one group has value
                checkAndClearGroupErrors(record.key, handler, "firstName", "");
                return;
              }

              const isCheckboxChecked = handler.formTab2.getFieldValue([
                `record${record.key}`,
                "isForeigner",
              ]);
              const isValid = validateInputName(
                value,
                (errorMessage: string) => {
                  handler.handleRowError(record.key, errorMessage, "firstName");
                },
                isCheckboxChecked,
                MESSAGE.MESSAGE_CCUS061_VALID_FIRST_NAME
              );

              handler.setNotSubmit(!isValid);

              // Check and clear group errors if at least one group has value
              checkAndClearGroupErrors(record.key, handler, "firstName", value);
            }}
          />
        </Form.Item>
      ),
      width: 170,
    },
    {
      title: "技能者姓(カタカナ)②",
      render: (_, record) => (
        <Form.Item
          name={[`record${record.key}`, `lastNameKatakana`]}
          className="input-ccus062-container"
          style={{
            paddingTop: 8,
            paddingLeft: 8,
          }}
          help={
            handler.rowErrors[record.key]?.lastNameKatakana
              ? handler.rowErrors[record.key]?.lastNameKatakana
              : ""
          }
          validateStatus={
            handler.rowErrors[record.key]?.lastNameKatakana ? "error" : ""
          }
        >
          <Input
            disabled={record.state === AUTHENTICATION_KEY[1].key}
            size="large"
            className="input-ccus062"
            style={{ width: 125 }}
            onInput={(e: any) => {
              const { value } = e.target;

              if (!value) {
                handler.handleRowError(record.key, "", "lastNameKatakana");
                handler.setNotSubmit(false);
                // Check and clear group errors if at least one group has value
                checkAndClearGroupErrors(record.key, handler, "lastNameKatakana", "");
                return;
              }

              const isValid = validateInputNameKatakana(
                value,
                (errorMessage: string) => {
                  handler.handleRowError(
                    record.key,
                    errorMessage,
                    "lastNameKatakana"
                  );
                }
              );

              handler.setNotSubmit(!isValid);

              // Check and clear group errors if at least one group has value
              checkAndClearGroupErrors(record.key, handler, "lastNameKatakana", value);
            }}
          />
        </Form.Item>
      ),
      width: 170,
    },
    {
      title: "技能者名(カタカナ)②",
      render: (_, record) => (
        <Form.Item
          name={[`record${record.key}`, `firstNameKatakana`]}
          className="input-ccus062-container"
          style={{
            paddingTop: 8,
            paddingLeft: 8,
          }}
          help={
            handler.rowErrors[record.key]?.firstNameKatakana
              ? handler.rowErrors[record.key]?.firstNameKatakana
              : ""
          }
          validateStatus={
            handler.rowErrors[record.key]?.firstNameKatakana ? "error" : ""
          }
        >
          <Input
            disabled={record.state === AUTHENTICATION_KEY[1].key}
            size="large"
            className="input-ccus062"
            style={{ width: 125 }}
            onInput={(e: any) => {
              const { value } = e.target;

              if (!value) {
                handler.handleRowError(record.key, "", "firstNameKatakana");
                handler.setNotSubmit(false);
                // Check and clear group errors if at least one group has value
                checkAndClearGroupErrors(record.key, handler, "firstNameKatakana", "");
                return;
              }

              const isValid = validateInputNameKatakana(
                value,
                (errorMessage: string) => {
                  handler.handleRowError(
                    record.key,
                    errorMessage,
                    "firstNameKatakana"
                  );
                }
              );

              handler.setNotSubmit(!isValid);

              // Check and clear group errors if at least one group has value
              checkAndClearGroupErrors(record.key, handler, "firstNameKatakana", value);
            }}
          />
        </Form.Item>
      ),
      width: 170,
    },
    {
      title: "本人確認番号③",
      render: (_, record) => (
        <Form.Item
          name={[`record${record.key}`, `confirmCode`]}
          className="input-ccus062-container"
          style={{
            paddingTop: 8,
            paddingLeft: 8,
          }}
          help={
            handler.rowErrors[record.key]?.confirmCode
              ? handler.rowErrors[record.key]?.confirmCode
              : ""
          }
          validateStatus={
            handler.rowErrors[record.key]?.confirmCode ? "error" : ""
          }
        >
          <Input
            disabled={record.state === AUTHENTICATION_KEY[1].key}
            size="large"
            className="input-ccus062"
            style={{ width: 125 }}
            maxLength={4}
            onInput={(e: any) => {
              let { value } = e.target;
              const originalValue = value;

              value = value.replace(/[^\d]/g, "");

              if (value.length > 4) {
                value = value.slice(0, 4);
              }

              e.target.value = value;

              // Get the actual current value from form for checking groups
              const currentFormValue = handler.formTab2.getFieldValue([`record${record.key}`, "confirmCode"]);

              // If user typed non-digit characters and value became empty, 
              // we should use the form's current value for group checking
              const valueForGroupCheck = value || currentFormValue || "";

              const regex = /^\d{4}$/;
              if (value && !regex.test(value)) {
                // Value has digits but not 4 digits yet
                handler.handleRowError(
                  record.key,
                  MESSAGE.MESSAGE_CCUS061_HALF_WIDTH_DIGIT_4,
                  "confirmCode"
                );
                handler.setNotSubmit(true);
              } else if (value) {
                // Value has exactly 4 digits - valid
                handler.handleRowError(record.key, "", "confirmCode");
                handler.setNotSubmit(false);
              }
              // If value is empty after filtering (user typed only non-digits),
              // don't change the current error state - let the existing error remain

              // Check and clear group errors if at least one group has value
              checkAndClearGroupErrors(record.key, handler, "confirmCode", valueForGroupCheck);
            }}
          />
        </Form.Item>
      ),
      width: 170,
    },
    {
      title: (
        <div>
          生年月日<span className="sterisk">*</span>
        </div>
      ),
      render: (_, record) => (
        <Form.Item
          name={[`record${record.key}`, `dateOfBirth`]}
          className="input-ccus062-container"
          style={{
            paddingTop: 8,
            paddingLeft: 8,
          }}
          help={
            handler.rowErrors[record.key]?.dateOfBirth
              ? handler.rowErrors[record.key]?.dateOfBirth
              : ""
          }
          validateStatus={
            handler.rowErrors[record.key]?.dateOfBirth ? "error" : ""
          }
        >
          <Input
            disabled={record.state === AUTHENTICATION_KEY[1].key}
            size="large"
            className="input-ccus062"
            style={{ width: "100px" }}
            maxLength={8}
            placeholder="yyyyMMdd"
            onInput={(e: any) => {
              let { value } = e.target;

              const validInputPattern = /[^\d]/g;
              value = value.replace(validInputPattern, "");

              if (value.length > 8) {
                value = value.slice(0, 8);
              }

              e.target.value = value;

              if (!value) {
                handler.handleRowError(
                  record.key,
                  MESSAGE.MESSAGE_REQUIRED,
                  "dateOfBirth"
                );
                handler.setNotSubmit(true);
              } else if (!/^\d{8}$/.test(value)) {
                handler.handleRowError(
                  record.key,
                  MESSAGE.MESSAGE_REQUIRED,
                  "dateOfBirth"
                );
                handler.setNotSubmit(true);
              } else {
                const [year, month, day] = [
                  value.slice(0, 4),
                  value.slice(4, 6),
                  value.slice(6, 8),
                ];

                const isValidDate = moment(
                  `${year}-${month}-${day}`,
                  "YYYY-MM-DD",
                  true
                ).isValid();

                if (
                  year > new Date().getFullYear() ||
                  !/^(0[1-9]|1[0-2])$/.test(month) ||
                  !isValidDate
                ) {
                  handler.handleRowError(
                    record.key,
                    MESSAGE.MESSAGE_REQUIRED,
                    "dateOfBirth"
                  );
                  handler.setNotSubmit(true);
                } else {
                  handler.handleRowError(record.key, "", "dateOfBirth"); // Clear error if valid
                  handler.setNotSubmit(false);
                }
              }
            }}
          />
        </Form.Item>
      ),
      width: 120,
    },
    {
      title: "認証結果",
      render: (_, record) => {
        return (
          <div style={{ padding: "16px 0px 16px 8px" }}>
            <Form.Item
              name={[`record${record.key}`, `state`]}
              hidden
              style={{ display: "none" }}
            >
              <Input
                disabled={record.state === AUTHENTICATION_KEY[1].key}
                size="large"
                className="input-ccus062"
              />
            </Form.Item>
            <div
              className={`${record.state === AUTHENTICATION_KEY[1].key
                ? "text-state-success"
                : record.state === AUTHENTICATION_KEY[2].key
                  ? "text-state-error"
                  : ""
                }`}
            >
              {record.state === AUTHENTICATION_KEY[1].key ? (
                AUTHENTICATION_KEY[1].value
              ) : record.state === AUTHENTICATION_KEY[2].key ? (
                <div className="record-state-table">
                  <span>{AUTHENTICATION_KEY[2].value}</span>{" "}
                  <img
                    src={IconInfoRed}
                    style={{ cursor: "pointer" }}
                    onClick={() => handler.openModalError(record)}
                  />
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
        );
      },
      width: 130,
    },
  ];
  const rowSelection = {
    selectedRowKeys: handler.selectedRowsKey,
    onChange: (selectedRowKeys: React.Key[]) => {
      handler.getSelectedKey(selectedRowKeys);
    },
    getCheckboxProps: (record: any) => {
      return {
        disabled: record.state === AUTHENTICATION_KEY[1].key,
      };
    },
  };
  return {
    columnsTab1,
    columnsTab2,
    rowSelection,
  };
};
